<script setup>
import { computed, nextTick } from "vue";
import { sleep } from "common/utils/helpers/delay";
import { useStore } from "vuex";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import SoftnessScoreMini from "components/compositions/softness_score_mini/component.vue";
import { pushUrl } from "common/utils/browser";
import { trackItemClick, trackPrintReviewsClick } from "../utils/tracker";
import Preview from "../previewsComponents/Preview.vue";

const SCROLL_GAP = 35;

const props = defineProps({
  group: {
    required: true,
    type: Object
  }
});

const store = useStore();
const product = computed(() => store.getters["product/getProductById"](props.group.product_id));
const composition = computed(() =>
  product.value.compositions.find(({ id }) => id === props.group.id)
);
const selectedProduct = computed(() => store.getters["product/selectedProduct"]);
const isUpdating = computed(() => store.getters["loading/currentOrder"]);
const isSingleProductPage = computed(() => store.getters["product/isSingleProductPage"]);

const sizeQuantities = computed(() =>
  Object.entries(props.group.variants).reduce((acc, [sizeId, quantity]) => {
    const { size } = composition.value.variants.find(({ id }) => String(id) === String(sizeId));
    acc[size] = quantity;
    return acc;
  }, {})
);

const handleEdit = async () => {
  const selectedProductId = selectedProduct.value?.id;

  store.dispatch("product/handleProductSelect", product.value);
  store.dispatch("product/handleCompositionSelect", composition.value);

  trackItemClick({
    action: "edit",
    product: product.value,
    composition: composition.value,
    group: props.group,
    sizes: Object.keys(sizeQuantities.value)
  });

  if (isSingleProductPage.value && composition.value.madePdpUrl) {
    pushUrl(composition.value.madePdpUrl, true);
  }

  await nextTick();

  if (selectedProductId !== product.value?.id) {
    // we have transition set to 500ms, adding more delay to make sure it's all rendered
    await sleep(750);
  }

  const $stickyHeader = document.querySelector(".js-artwork-sticky-box");
  const offset = ($stickyHeader?.offsetHeight || 0) + SCROLL_GAP;
  ScrollToElement.scrollIntoViewBySelector(".js-selected-composition-scroll-target");
  ScrollToElement.scrollToElementBySelector(".js-selected-composition-scroll-target", offset);
};

const handleReviewClick = () => {
  trackPrintReviewsClick(product.value);
};

const handleRemove = () => {
  if (isUpdating.value) return;

  store.dispatch("product/removeComposition", props.group.id);

  trackItemClick({
    action: "remove",
    product: product.value,
    composition: composition.value,
    group: props.group,
    sizes: Object.keys(sizeQuantities.value)
  });
};
</script>

<template>
  <div class="product-preview">
    <div class="product-preview__side product-preview__side--image">
      <div class="product-preview__image-wrap">
        <Preview
          class="product-preview__image"
          :composition-id="composition.id"
          :product-id="product.id"
          :default-image-url="composition.imageUrl"
          :preview="props.group"
        />
      </div>
    </div>

    <div class="product-preview__side product-preview__side--info">
      <div class="product-preview__rating" @click="handleReviewClick">
        <UseSvg icon="star-fill" class="product-preview__rating-icon" />
        <span class="product-preview__rating-score">{{ product.averageRating }}</span>
        <span class="product-preview__rating-reviews">({{ product.reviewsCount }})</span>
      </div>

      <div class="product-preview__softness-score">
        <SoftnessScoreMini :value="product.cozyfeelScore" />
      </div>

      <h3 class="product-preview__name">{{ product.name }}</h3>

      <dl class="product-preview__params">
        <dt class="product-preview__params-title">Color:</dt>
        <dd class="product-preview__params-data">{{ composition.colorName }}</dd>
      </dl>

      <dl class="product-preview__params">
        <dt class="product-preview__params-title">Size / quantity:</dt>
        <dd class="product-preview__params-data">
          <span
            v-for="(quantity, size) in sizeQuantities"
            :key="size"
            class="product-preview__param"
          >
            <span class="product-preview__param-value">{{ size }} / {{ quantity }}</span>
            <span class="product-preview__param-separator">,</span>
          </span>
        </dd>
      </dl>

      <div class="product-preview__actions">
        <button
          class="product-preview__button product-preview__button--edit"
          type="button"
          @click="handleEdit"
        >
          Edit
        </button>
        <button
          class="product-preview__button"
          :class="{ 'product-preview__button--loading': isUpdating }"
          type="button"
          @click="handleRemove"
        >
          Remove
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";

.product-preview {
  $this: &;

  display: grid;
  grid-template-columns: 75px 1fr;
  gap: 10px;
  position: relative;
  min-height: 141px;
  padding: 8px;
  border: 1.5px solid $pod-gray-modern-200;
  border-radius: 6px;

  &__image-wrap {
    min-height: 75px;
    background-color: $pod-gray-modern-100;
    border-radius: 8px;
  }

  &__image {
    display: block;
    width: 100%;
    border-radius: 8px;
  }

  &__softness-score {
    display: inline-block;
  }

  :deep(.softness-score-mini) {
    width: auto;
  }

  :deep(.softness-score-mini__label) {
    letter-spacing: -1px;
  }

  :deep(.softness-score-mini__number) {
    width: auto;
    line-height: 18px;
  }

  &__rating {
    display: flex;
    align-items: center;
    color: $pod-gray-modern-600;
    font-size: 12px;
  }

  &__rating-icon {
    color: $accent-grapefruit;
    width: 12px;
    height: 12px;
    margin-right: 2px;
  }

  &__rating-score {
    margin-right: 6px;
    font-weight: 500;
  }

  &__name {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: $pod-gray-modern-800;
  }

  &__params {
    font-size: 13px;
    font-weight: 500;
  }

  &__params-title {
    float: left;
    margin-right: 7px;
    color: $pod-gray-modern-500;
  }

  &__params-data {
    color: $pod-gray-modern-700;
  }

  &__param {
    display: inline-block;
    white-space: nowrap;

    &:last-of-type {
      #{$this}__param-separator {
        display: none;
      }
    }
  }

  &__param-separator {
    margin-right: 4px;
  }

  &__actions {
    display: flex;
    gap: 10px;
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__button {
    padding: 4px;
    border: 0;
    border-radius: 6px;
    background-color: $pod-titan;
    color: $blue;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:focus,
    &:hover {
      background-color: darken($pod-titan, 5%);
    }

    &--loading {
      background-color: $pod-gray-modern-200;
      cursor: wait;

      &:focus,
      &:hover {
        background-color: $pod-gray-modern-200;
      }
    }
  }
}
</style>
